import { FolderIcon, TrashIcon, XIcon } from "@heroicons/react/outline";
import { AnimatePresence, motion } from "framer-motion";
import { observer } from "mobx-react-lite";

import { useRegistryDialogs } from "./Dialogs";
import { useUnifiedRegistry } from "./unified-registry-context";

import { BasicTooltip } from "@/components/ui/Tooltip";
import { Button } from "@/components/ui/button";
import { useRbac } from "@/providers/RbacProvider";

export const SelectionToolbar: React.FC = observer(() => {
  const registry = useUnifiedRegistry();
  const { openMoveFolder, deleteBatch } = useRegistryDialogs();
  const { isEditDisabled, isDeleteDisabled } = useRbac();

  const bulkMoveDisabled = registry.selectedEntities.some(entity => isEditDisabled(entity.type));
  const bulkDeleteDisabled = registry.selectedEntities.some(entity =>
    isDeleteDisabled(entity.type),
  );

  if (registry.multiSelectMode) {
    return null;
  }

  return (
    <AnimatePresence>
      {registry.selectedCount > 0 && (
        <motion.div
          className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 transform"
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-lg">
            <span className="text-sm font-medium text-gray-700">
              {registry.selectedCount} selected
            </span>

            <div className="h-6 w-px bg-gray-300" />

            <BasicTooltip
              content="You don't have permission to move one or more selected items"
              disabled={!bulkMoveDisabled}
            >
              <span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openMoveFolder(registry.selectedEntities)}
                  className="gap-2"
                  disabled={bulkMoveDisabled}
                >
                  <FolderIcon className="h-4 w-4" />
                  Move
                </Button>
              </span>
            </BasicTooltip>

            <BasicTooltip
              content="You don't have permission to delete one or more selected items"
              disabled={!bulkDeleteDisabled}
            >
              <span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    await deleteBatch(registry.selectedEntities, () => {
                      registry.clearSelection();
                    });
                  }}
                  className="gap-2 text-red-600 hover:bg-red-50 hover:text-red-700"
                  disabled={bulkDeleteDisabled}
                >
                  <TrashIcon className="h-4 w-4" />
                  Delete
                </Button>
              </span>
            </BasicTooltip>

            <div className="h-6 w-px bg-gray-300" />

            <Button
              size="sm"
              variant="ghost"
              onClick={() => registry.clearSelection()}
              className="gap-2"
            >
              <XIcon className="h-4 w-4" />
              Clear
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});
